import axios from "axios";
import api from "../api/api";
import { BASE_URL } from "../api/setup";
import { Exame } from "../types/exames";
import { Callback } from "../types/general";
import useExameStore from "../zustand/use-exame-store";

function useExameModel() {

  const {
    exames,
    setExames,
    setSelectedExame,
    resetState,
    removeExame,
    updateExame,
  } = useExameStore();

  // Fetch all exames with pagination
  const fetchExames = async (page: number = 1) => {
    try {
      const response = await api.get({
        path: "/api/exames",
        params: { page },
      }, (data: any) => {
        setExames(data);
        console.log(exames)
      }, (error: any) => {
        console.error("Error fetching exames:", error);
      });

      return response;
    } catch (error) {
      console.error("Error fetching exames:", error);
      throw error;
    }
  };

  // Fetch exames by school
  const fetchExamesBySchool = async (schoolId: number, page: number = 1) => {
    try {
      const response = await api.get({
        path: "/api/exames/school",
        params: { school: schoolId, page },
      }, (data: any) => {
        setExames(data);
      }, (error: any) => {
        console.error("Error fetching exames by school:", error);
      });

      return response;
    } catch (error) {
      console.error("Error fetching exames by school:", error);
      throw error;
    }
  };

  // Fetch suggested exames
  const fetchSuggestions = async (page: number = 1) => {
    try {
      const response = await api.get({
        path: "/api/exames/suggestions",
        params: { page },
      }, (data: any) => {
        setExames(data);
      }, (error: any) => {
        console.error("Error fetching suggestions:", error);
      });

      return response;
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      throw error;
    }
  };

  // Create new exame
  const createExame = async (
    data: {
      title: string;
      year: number;
      url?: any;
      data?: string;
      visible?: boolean;
      author?: string;
      schoolId: number
    },
    callback?: Callback
  ) => {
    try {
      // If file is provided, use FormData
      const formData = new FormData();
      formData.append('title', data.title);
      formData.append('year', data.year.toString());
      if (data.url) formData.append('url', data.url);
      if (data.data) formData.append('data', data.data);
      if (data.visible !== undefined) formData.append('visible', data.visible.toString());
      if (data.author) formData.append('author', data.author);

      const response = await axios.post(`${BASE_URL}/api/exames`, formData, {
        params: { school: data.schoolId }
      });

      setExames({
        ...exames,
        data: [...[response.data], ...exames.data]
      })
      callback?.response?.(response.data);
      return response.data;
    } catch (error) {
      console.error("Error creating exame:", error);
      callback?.error?.(error);
      throw error;
    }
  };

  // Suggest new exame (visible = false)
  const suggestExame = async (
    data: {
      title: string;
      year: number;
      url?: any;
      data?: string;
      author?: string;
      schoolId: number;
    },
    callback?: Callback
  ) => {
    try {
      // If file is provided, use FormData
      const formData = new FormData();
      formData.append('title', data.title);
      formData.append('year', data.year.toString());
      if (data.url) formData.append('url', data.url);
      if (data.data) formData.append('data', data.data);
      if (data.author) formData.append('author', data.author);

      api.post({
        path: "/api/exames/suggest",
        params: { school: data.schoolId },
        data: formData,
      }, (responseData: any) => {
        callback?.response?.(responseData);
      }, (error: any) => {
        callback?.error?.(error);
      });

    } catch (error) {
      console.error("Error suggesting exame:", error);
      throw error;
    }
  };

  // Update existing exame
  const updateExameById = async (
    exame: Exame,
    callback?: Callback
  ) => {
    try {
      api.put({
        path: "/api/exames",
        params: { school: exame.school_id, exame: exame.id },
        data: {
          title: exame.title,
          year: exame.year,
          url: exame.url,
          data: exame.data,
          visible: exame.visible,
          author: exame.author,
        },
      }, (data: any) => {
        callback?.response?.(data);
        updateExame(data);
      }, (error: any) => {
        console.log(error)
        callback?.error?.(error);
      });

    } catch (error) {
      console.error("Error updating exame:", error);
      throw error;
    }
  };

  // Validate/approve exame
  const validateExame = async (id: number, callback?: Callback) => {
    try {
      api.put({
        path: `/api/exames/validate/${id}`,
      }, (data: any) => {
        callback?.response?.(data);
        updateExame({
          ...(exames.data.find((item) => item.id === id) as Exame),
          visible: true
        })
      }, (error: any) => {
        callback?.error?.(error);
      });
    } catch (error) {
      console.error("Error validating exame:", error);
      throw error;
    }
  };

  // Reject exame
  const rejectExame = async (id: number, callback?: Callback) => {
    try {
      api.put({
        path: `/api/exames/reject/${id}`,
      }, (data: any) => {
        callback?.response?.(data);
        updateExame({
          ...(exames.data.find((item) => item.id === id) as Exame),
          visible: false
        })
      }, (error: any) => {
        callback?.error?.(error);
      });
    } catch (error) {
      console.error("Error rejecting exame:", error);
      throw error;
    }
  };

  // Delete exame
  const deleteExameById = async (id: number, callback: Callback) => {
    try {
      await api.delete({
        path: "/api/exames",
        params: { exame: id },
      }, (data: any) => {
        callback.response?.(data);
        removeExame(id);
      }, (error: any) => {
        callback.error?.(error);
      });
    } catch (error) {
      console.error("Error deleting exame:", error);
      throw error;
    }
  };

  return {
    fetchExames,
    fetchExamesBySchool,
    fetchSuggestions,
    createExame,
    suggestExame,
    updateExameById,
    validateExame,
    rejectExame,
    deleteExameById,
  };
}

export default useExameModel;
